import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { boardKeys } from "@/application/query-keys/board.keys";
import {
  boardRepository,
  type CreateBoardFeatureInput,
  type CreateWorkstreamInput,
} from "@/infrastructure/repositories/board.supabase.repository";
import type {
  BoardActivityRow,
  BoardFeatureDependencyRow,
  BoardFeatureRow,
  MilestoneRow,
  WorkstreamRow,
} from "@/infrastructure/supabase/database.types";

const STATIC = { staleTime: 5 * 60 * 1000 };

export function useWorkstreams() {
  return useQuery({
    queryKey: boardKeys.workstreams(),
    queryFn: () => boardRepository.listWorkstreams(),
    ...STATIC,
  });
}

export function useMilestones() {
  return useQuery({
    queryKey: boardKeys.milestones(),
    queryFn: () => boardRepository.listMilestones(),
    ...STATIC,
  });
}

// Edit a workstream's name and/or Gantt dates from the timeline (BF-198, BF-213).
// Optimistically patches the cached workstreams list so the lane updates immediately;
// rolls back on error (e.g. RLS rejects a view-only user).
export function useUpdateWorkstream() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      key,
      patch,
    }: {
      key: string;
      patch: Partial<Pick<WorkstreamRow, "title" | "start_date" | "end_date">>;
    }) => boardRepository.updateWorkstream(key, patch),
    onMutate: async ({ key, patch }) => {
      // Cancel any in-flight workstreams refetch first, so a late response can't
      // overwrite the optimistic patch (standard TanStack optimistic pattern).
      await queryClient.cancelQueries({ queryKey: boardKeys.workstreams() });
      const prev = queryClient.getQueryData<WorkstreamRow[]>(boardKeys.workstreams());
      queryClient.setQueryData<WorkstreamRow[]>(boardKeys.workstreams(), (old) =>
        old ? old.map((w) => (w.key === key ? { ...w, ...patch } : w)) : old
      );
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(boardKeys.workstreams(), ctx.prev);
      console.error("[useUpdateWorkstream]", err);
    },
    onSuccess: (row) => {
      queryClient.setQueryData<WorkstreamRow[]>(boardKeys.workstreams(), (old) =>
        old ? old.map((w) => (w.key === row.key ? row : w)) : old
      );
    },
  });
}

// Create a workstream from the implementation timeline. Merges the new row into
// the cached workstreams list (kept sorted by sort_order) so the gantt lane and
// the board group both appear without a refetch.
export function useCreateWorkstream() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWorkstreamInput) => boardRepository.createWorkstream(input),
    onSuccess: (row) => {
      queryClient.setQueryData<WorkstreamRow[]>(boardKeys.workstreams(), (old) =>
        old ? [...old, row].sort((a, b) => a.sort_order - b.sort_order) : [row]
      );
    },
  });
}

// Edit a milestone's target date from the timeline. Optimistically patches the
// cached milestones list so the diamond moves immediately; rolls back on error
// (e.g. RLS rejects a view-only user). Mirrors useUpdateWorkstreamDates.
export function useUpdateMilestone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, patch }: { key: string; patch: Pick<MilestoneRow, "target_date"> }) =>
      boardRepository.updateMilestone(key, patch),
    onMutate: async ({ key, patch }) => {
      await queryClient.cancelQueries({ queryKey: boardKeys.milestones() });
      const prev = queryClient.getQueryData<MilestoneRow[]>(boardKeys.milestones());
      queryClient.setQueryData<MilestoneRow[]>(boardKeys.milestones(), (old) =>
        old ? old.map((m) => (m.key === key ? { ...m, ...patch } : m)) : old
      );
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(boardKeys.milestones(), ctx.prev);
      console.error("[useUpdateMilestone]", err);
    },
    onSuccess: (row) => {
      queryClient.setQueryData<MilestoneRow[]>(boardKeys.milestones(), (old) =>
        old ? old.map((m) => (m.key === row.key ? row : m)) : old
      );
    },
  });
}

export function useMvpComponents() {
  return useQuery({
    queryKey: boardKeys.components(),
    queryFn: () => boardRepository.listComponents(),
    ...STATIC,
  });
}

export function useComponentDependencies() {
  return useQuery({
    queryKey: boardKeys.dependencies(),
    queryFn: () => boardRepository.listDependencies(),
    ...STATIC,
  });
}

export function useBoardFeatures(options?: { subscribed?: boolean }) {
  return useQuery({
    queryKey: boardKeys.features(),
    queryFn: () => boardRepository.listFeatures(),
    // When subscribed === false the hook returns current cached data but does NOT
    // re-render on future changes. The dependency pickers use this so closed
    // pickers stay inert while a feature edit updates the cache.
    subscribed: options?.subscribed,
  });
}

export function useBoardFeatureDetail(id: string | undefined) {
  return useQuery({
    queryKey: boardKeys.feature(id ?? ""),
    queryFn: () => boardRepository.featureById(id as string),
    enabled: !!id,
  });
}

export function useUpdateBoardFeature() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<BoardFeatureRow> }) =>
      boardRepository.updateFeature(id, patch),
    onMutate: ({ id, patch }) => {
      const prev = queryClient.getQueryData<BoardFeatureRow[]>(boardKeys.features());
      const prevDetail = queryClient.getQueryData<BoardFeatureRow>(boardKeys.feature(id));
      queryClient.setQueryData<BoardFeatureRow[]>(boardKeys.features(), (old) =>
        old ? old.map((f) => (f.id === id ? { ...f, ...patch } : f)) : old
      );
      if (prevDetail) {
        queryClient.setQueryData(boardKeys.feature(id), { ...prevDetail, ...patch });
      }
      return { prev, prevDetail };
    },
    onError: (err, { id }, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(boardKeys.features(), ctx.prev);
      if (ctx?.prevDetail) queryClient.setQueryData(boardKeys.feature(id), ctx.prevDetail);
      console.error("[useUpdateBoardFeature]", err);
    },
    onSuccess: (row) => {
      queryClient.setQueryData<BoardFeatureRow[]>(boardKeys.features(), (old) =>
        old ? old.map((f) => (f.id === row.id ? row : f)) : old
      );
      queryClient.setQueryData(boardKeys.feature(row.id), row);
    },
    // The audit log is written by a DB trigger; pull the new entries after the write.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: boardKeys.activity() });
    },
  });
}

export function useCreateBoardFeature() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBoardFeatureInput) => boardRepository.createFeature(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: boardKeys.features() });
      void queryClient.invalidateQueries({ queryKey: boardKeys.activity() });
    },
  });
}

export function useBoardFeatureDependencies() {
  return useQuery({
    queryKey: boardKeys.featureDependencies(),
    queryFn: () => boardRepository.listFeatureDependencies(),
  });
}

const EMPTY_IDS: string[] = [];

/**
 * Just one feature's prerequisite ids. `select` returns a sorted array so React
 * Query's structural sharing keeps a stable reference — a picker only re-renders
 * when ITS own dependencies change, not when any feature's do. This is what keeps
 * a dependency toggle from re-rendering the whole board.
 */
export function useFeatureDependencyIds(featureId: string): string[] {
  return (
    useQuery({
      queryKey: boardKeys.featureDependencies(),
      queryFn: () => boardRepository.listFeatureDependencies(),
      select: (rows) =>
        rows
          .filter((d) => d.feature_id === featureId)
          .map((d) => d.depends_on_feature_id)
          .sort(),
    }).data ?? EMPTY_IDS
  );
}

export function useAddBoardDependencies() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ featureId, dependsOnIds }: { featureId: string; dependsOnIds: string[] }) =>
      boardRepository.addFeatureDependencies(featureId, dependsOnIds),
    onMutate: ({ featureId, dependsOnIds }) => {
      // Cancel any in-flight refetch (fire-and-forget) so it can't clobber the
      // optimistic write — but do NOT await it, or the UI update is delayed.
      void queryClient.cancelQueries({ queryKey: boardKeys.featureDependencies() });
      const prev = queryClient.getQueryData<BoardFeatureDependencyRow[]>(
        boardKeys.featureDependencies()
      );
      queryClient.setQueryData<BoardFeatureDependencyRow[]>(
        boardKeys.featureDependencies(),
        (old) => {
          const list = old ?? [];
          const existing = new Set(
            list.filter((d) => d.feature_id === featureId).map((d) => d.depends_on_feature_id)
          );
          const additions = dependsOnIds
            .filter((id) => !existing.has(id))
            .map((id) => ({
              id: `optimistic-${featureId}-${id}`,
              feature_id: featureId,
              depends_on_feature_id: id,
              created_at: "",
            }));
          return [...list, ...additions];
        }
      );
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(boardKeys.featureDependencies(), ctx.prev);
      console.error("[useAddBoardDependencies]", err);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: boardKeys.featureDependencies() });
      void queryClient.invalidateQueries({ queryKey: boardKeys.activity() });
    },
  });
}

export function useRemoveBoardDependencies() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ featureId, dependsOnIds }: { featureId: string; dependsOnIds: string[] }) =>
      boardRepository.removeFeatureDependencies(featureId, dependsOnIds),
    onMutate: ({ featureId, dependsOnIds }) => {
      void queryClient.cancelQueries({ queryKey: boardKeys.featureDependencies() });
      const prev = queryClient.getQueryData<BoardFeatureDependencyRow[]>(
        boardKeys.featureDependencies()
      );
      const drop = new Set(dependsOnIds);
      queryClient.setQueryData<BoardFeatureDependencyRow[]>(
        boardKeys.featureDependencies(),
        (old) =>
          (old ?? []).filter(
            (d) => !(d.feature_id === featureId && drop.has(d.depends_on_feature_id))
          )
      );
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(boardKeys.featureDependencies(), ctx.prev);
      console.error("[useRemoveBoardDependencies]", err);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: boardKeys.featureDependencies() });
      void queryClient.invalidateQueries({ queryKey: boardKeys.activity() });
    },
  });
}

export function useDeleteBoardFeature() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => boardRepository.deleteFeature(id),
    onMutate: (id) => {
      const prev = queryClient.getQueryData<BoardFeatureRow[]>(boardKeys.features());
      queryClient.setQueryData<BoardFeatureRow[]>(boardKeys.features(), (old) =>
        old ? old.filter((f) => f.id !== id) : old
      );
      return { prev };
    },
    onError: (err, _id, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(boardKeys.features(), ctx.prev);
      console.error("[useDeleteBoardFeature]", err);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: boardKeys.activity() });
    },
  });
}

const ACTIVITY_PAGE = 50;

export function useFeatureActivity(id: string | undefined) {
  return useInfiniteQuery({
    queryKey: boardKeys.featureActivity(id ?? ""),
    queryFn: ({ pageParam }) =>
      boardRepository.listActivity({ featureId: id, cursor: pageParam, limit: ACTIVITY_PAGE }),
    initialPageParam: null as number | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: !!id,
  });
}

export function useBoardActivity(options?: { enabled?: boolean }) {
  return useInfiniteQuery({
    queryKey: boardKeys.activity(),
    queryFn: ({ pageParam }) =>
      boardRepository.listActivity({ cursor: pageParam, limit: ACTIVITY_PAGE }),
    initialPageParam: null as number | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: options?.enabled ?? true,
  });
}

export type { BoardActivityRow };
